import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import {
  Type,
  cssColorValue,
  semanticColors,
  spacer12,
  spacer4,
  spacer8,
} from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { NavigationLink } from '@components/shared/ResultsTable/NavigationLink';
import { dateFromProtoTimestamp } from '@helpers/utils';
import { AlbumMetadata } from '@generated/grpc/spotify/artistwatchlist/v1';
import {
  APPROVAL_REASON,
  getPopoverDescription,
  getReasonLabels,
} from '../constants';
import { HeldReleasesTableRowTooltip } from './HeldReleasesTableRowTooltip';

const TextBoxTooltip = styled.div`
  padding: ${spacer8};
  background-color: ${cssColorValue(
    semanticColors.backgroundElevatedHighlight,
  )};
  display: flex;
  flex-direction: column;
  gap: ${spacer4};
`;

type DateSubmittedCellProps = {
  row: AlbumMetadata;
};

export const DateSubmittedCell: FunctionComponent<
  DateSubmittedCellProps
> = props => {
  const { row } = props;

  const t = useT();
  const REASON_LABEL = getReasonLabels(t);
  const POPOVER_DESCRIPTION = getPopoverDescription(t);

  if (!!row.dateSubmitted) {
    return (
      <Type variant="viola" semanticColor="textSubdued">
        <HeldReleasesTableRowTooltip
          id="blocked-reason-tooltip"
          overlay={
            <Stack gap={spacer12}>
              <Type variant="mesto">
                {POPOVER_DESCRIPTION[row.reasonSubmitted as APPROVAL_REASON]}
              </Type>
              <TextBoxTooltip>
                <Type variant="finaleBold">
                  {t(
                    'atlas.held_releases.date_submitted_popover.reason_selected',
                    'Reason selected',
                    'Popover title to show the selected reason of approval',
                  )}
                </Type>
                <Type variant="finale">
                  {REASON_LABEL[row.reasonSubmitted as APPROVAL_REASON]}
                </Type>
              </TextBoxTooltip>
            </Stack>
          }
        >
          {dateFromProtoTimestamp(row.dateSubmitted).date}
        </HeldReleasesTableRowTooltip>
      </Type>
    );
  }
  return (
    <NavigationLink albumUri={row.albumUri!}>
      <Type variant="viola" semanticColor="textSubdued">
        {t(
          'atlas.held_releases.date_submitted_empty_state',
          'Not submitted',
          'Message to show when an album request for approval was not submitted',
        )}
      </Type>
    </NavigationLink>
  );
};
